/* sockelleisten-promo.js
 *
 * Strict eligibility:
 * - We rely ONLY on line-item properties we control on add-to-cart:
 *   _role === "floor"
 *   _m2 > 0
 *   _floor_base in ["vinylboden","laminat","parkett"]
 *
 * Opt-out preserved:
 * - If customer manually removes promo sockel, we set _sockel_optout="1" on the related floor line.
 * - Sockel won't be auto-added again unless the floor product is added again as a NEW line.
 *
 * Calculation rules:
 * - m² always rounded UP to integer (ceil)
 * - Sockel pieces: ceil( m²_int / 2.38 )
 * - When customer changes Pakete quantity in cart:
 *   effective_m2 = ceil( (m2_at_add / qty_at_add) * current_qty )
 *   then pieces = ceil( effective_m2 / 2.38 )
 */

(() => {
  // Guard: prevent double init
  if (window.__sockelPromoLoaded) return;
  window.__sockelPromoLoaded = true;

  const SOCKEL_VARIANT_ID = 52525046825298;
  const PIECE_DIVISOR = 2.38;

  const FLOOR_ROLE = "floor";
  const SOCKEL_ROLE = "sockelleiste";

  const ALLOWED_FLOOR_BASES = new Set(["vinylboden", "laminat", "parkett"]);

  const BLOCKED_SOURCE_COLLECTIONS = new Set([
    "bodentrends",
    "angebote",
    "alle-bodenbelage"
  ]);

  const SYNC_DEBOUNCE_MS = 200;

  let syncScheduled = false;
  let isSyncing = false;

  // Track external (non-sockel) cart mutation requests to avoid races
  let __sockel_externalMutationCount = 0;
  let __sockel_externalMutationPromise = Promise.resolve();

  function trackExternalMutation(promise) {
    __sockel_externalMutationCount++;
    __sockel_externalMutationPromise = promise.finally(() => {
      __sockel_externalMutationCount = Math.max(0, __sockel_externalMutationCount - 1);
    });
  }

  async function waitForExternalMutations(timeout = 800) {
    if (__sockel_externalMutationCount === 0) return;
    await Promise.race([
      __sockel_externalMutationPromise,
      new Promise((res) => setTimeout(res, timeout))
    ]).catch(() => {});
    // micro delay
    await new Promise((res) => setTimeout(res, 20));
  }

  function toNumber(val) {
    if (val == null) return 0;
    if (typeof val === "number") return val;
    const s = String(val).replace(",", ".").trim();
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  }

  function normalizeBase(raw) {
    return String(raw || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[^\w\s-]/g, "")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "-");
  }

  function isEligibleFloorLine(item) {
    const p = item && item.properties ? item.properties : {};

    // Opt-out from manual removal
    if (String(p._sockel_optout || "") === "1") return false;

    // Must be our floor line
    if (String(p._role || "") !== FLOOR_ROLE) return false;

    const m2 = toNumber(p._m2);
    if (!(m2 > 0)) return false;

    const src = normalizeBase(p._src_collection);
    if (src && BLOCKED_SOURCE_COLLECTIONS.has(src)) return false;

    const base = normalizeBase(p._floor_base);
    if (!ALLOWED_FLOOR_BASES.has(base)) return false;

    return true;
  }

  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error("cart.js failed: " + res.status);
    return res.json();
  }

  async function cartChange(lineKey, quantity) {
    await waitForExternalMutations();

    const res = await fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      __sockelInternal: true,
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ id: lineKey, quantity })
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error("cart/change.js failed: " + res.status + " " + text);
    }
    return res.json();
  }

  async function cartAdd(variantId, quantity, properties = {}) {
    await waitForExternalMutations();

    const payload = {
      id: Number(variantId),
      quantity: Number(quantity),
      properties
    };

    const res = await fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      __sockelInternal: true,
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error("cart/add.js failed: " + res.status + " " + text);
    }

    return res.json();
  }

  function scheduleSync(reason = "") {
    if (syncScheduled) return;
    syncScheduled = true;

    setTimeout(() => {
      syncScheduled = false;
      syncSockelleisten(reason).catch((e) => console.warn("[sockel] sync error:", e));
    }, SYNC_DEBOUNCE_MS);
  }

  /**
   * SAFE drawer refresh:
   * do NOT replace innerHTML (to avoid theme glitches / redirects)
   */
  async function refreshCartDrawerSafe() {
    const drawer = document.querySelector("cart-drawer, #cart-drawer");
    if (!drawer) return;

    try {
      const cart = await fetchCart().catch(() => null);
      if (cart) {
        if (typeof drawer.renderContents === "function") {
          drawer.renderContents(cart);
          return;
        }
        if (typeof drawer.render === "function") {
          drawer.render(cart);
          return;
        }
      }
    } catch (_) {}

    try {
      drawer.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      drawer.dispatchEvent(new CustomEvent("cart:change", { bubbles: true }));
      drawer.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));
    } catch (_) {}
  }

  /**
   * Manual remove helpers (opt-out)
   */
  async function recreateLineWithExtraProp(item, extraProps = {}) {
    const props = { ...(item.properties || {}), ...extraProps };
    await cartChange(item.key, 0);
    await cartAdd(item.variant_id, item.quantity, props);
  }

  async function handleManualRemovePromoSockel(lineKey) {
    const cart = await fetchCart();
    const items = cart.items || [];

    const sockel = items.find((it) => String(it.key) === String(lineKey));
    if (!sockel) return;

    const sp = sockel.properties || {};
    const floorKey = sp._for_key ? String(sp._for_key) : "";

    // 1) delete Sockel
    await cartChange(sockel.key, 0);

    // 2) set opt-out on related floor line
    if (floorKey) {
      const floor = items.find((it) => String(it.key) === floorKey);
      if (floor && floor.properties && String(floor.properties._role || "") === FLOOR_ROLE) {
        await recreateLineWithExtraProp(floor, { _sockel_optout: "1" });
      }
    }

    await refreshCartDrawerSafe();
    setTimeout(() => scheduleSync("manual-remove-sockel"), 50);
  }

  /**
   * Compute effective m² for a floor item:
   * effective_m2 = ceil( (m2_at_add / qty_at_add) * current_qty )
   * pieces = ceil( effective_m2 / 2.38 )
   */
  function computeForFloorItem(floorItem) {
    const p = floorItem.properties || {};

    // Prefer explicit baseline props if present
    const m2AtAddRaw =
      (p._m2_at_add != null && p._m2_at_add !== "") ? p._m2_at_add : p._m2;

    const qtyAtAddRaw =
      (p._qty_at_add != null && p._qty_at_add !== "") ? p._qty_at_add : floorItem.quantity;

    const m2AtAdd = Math.max(0, toNumber(m2AtAddRaw));
    const qtyAtAdd = Math.max(1, Math.round(toNumber(qtyAtAddRaw)));

    const currentQty = Math.max(1, Math.round(toNumber(floorItem.quantity)));
    const perUnitM2 = qtyAtAdd > 0 ? (m2AtAdd / qtyAtAdd) : 0;

    // m² always ceil to integer
    const m2Int = Math.ceil(perUnitM2 * currentQty);

    if (!(m2Int > 0)) return { m2Int: 0, pieces: 0 };

    // pieces always ceil
    const pieces = Math.ceil(m2Int / PIECE_DIVISOR);

    return { m2Int, pieces };
  }

  function sockelMatchesDesiredProps(sockelItem, desired) {
    const p = sockelItem.properties || {};
    return (
      String(p._for_key || "") === String(desired._for_key || "") &&
      String(p._m2_floor || "") === String(desired._m2_floor || "") &&
      String(p._for_floor_variant || "") === String(desired._for_floor_variant || "")
    );
  }

  async function syncSockelleisten(reason = "") {
    if (isSyncing) return;
    isSyncing = true;

    let didMutate = false;

    try {
      const cart = await fetchCart();
      const items = cart.items || [];

      // eligible floor lines only
      const floorLines = items.filter(isEligibleFloorLine);

      // auto sockel lines only (ours)
      const sockelLines = items.filter((it) => {
        const props = it.properties || {};
        return (
          Number(it.variant_id) === Number(SOCKEL_VARIANT_ID) &&
          props._role === SOCKEL_ROLE &&
          props._auto === "1" &&
          !!props._for_key
        );
      });

      const sockelByFloorKey = new Map();
      for (const s of sockelLines) {
        const fk = (s.properties || {})._for_key;
        if (fk) sockelByFloorKey.set(String(fk), s);
      }

      // remove sockel if floor disappeared or no longer eligible
      const floorKeys = new Set(floorLines.map((f) => String(f.key)));
      for (const s of sockelLines) {
        const fk = String((s.properties || {})._for_key || "");
        if (fk && !floorKeys.has(fk)) {
          await cartChange(s.key, 0);
          didMutate = true;
        }
      }

      // ensure correct sockel per eligible floor
      for (const floor of floorLines) {
        const floorKey = String(floor.key);

        const { m2Int, pieces } = computeForFloorItem(floor);
        const existingSockel = sockelByFloorKey.get(floorKey);

        if (pieces <= 0) {
          if (existingSockel) {
            await cartChange(existingSockel.key, 0);
            didMutate = true;
          }
          continue;
        }

        const desiredProps = {
          _role: SOCKEL_ROLE,
          _auto: "1",
          _for_key: floorKey,
          _for_floor_variant: String(floor.variant_id),
          _for_title: String(floor.product_title || floor.title || ""),
          // IMPORTANT: store m² as integer (ceil)
          _m2_floor: String(m2Int)
        };

        if (!existingSockel) {
          await cartAdd(SOCKEL_VARIANT_ID, pieces, desiredProps);
          didMutate = true;
          continue;
        }

        const qtyMismatch = Number(existingSockel.quantity) !== Number(pieces);
        const propsMismatch = !sockelMatchesDesiredProps(existingSockel, desiredProps);

        if (qtyMismatch || propsMismatch) {
          await cartChange(existingSockel.key, 0);
          await cartAdd(SOCKEL_VARIANT_ID, pieces, desiredProps);
          didMutate = true;
        }
      }

      if (didMutate) {
        await refreshCartDrawerSafe();
      }
    } finally {
      isSyncing = false;
    }
  }

  function patchFetch() {
    const originalFetch = window.fetch;

    window.fetch = async (input, init = {}) => {
      const url =
        typeof input === "string" ? input : (input && input.url ? input.url : "");

      const isCartMutationLocal =
        url.includes("/cart/add") ||
        url.includes("/cart/change") ||
        url.includes("/cart/update") ||
        url.includes("/cart/clear");

      const call = originalFetch(input, init);

      // Track external mutations (not ours)
      try {
        if (isCartMutationLocal && !(init && init.__sockelInternal)) {
          trackExternalMutation(call.catch(() => {}));
        }
      } catch (_) {}

      const res = await call;

      try {
        if (init && init.__sockelInternal) return res;

        const url2 =
          typeof input === "string" ? input : (input && input.url ? input.url : "");

        const isCartMutation =
          url2.includes("/cart/add") ||
          url2.includes("/cart/change") ||
          url2.includes("/cart/update") ||
          url2.includes("/cart/clear");

        if (isCartMutation && res && res.ok) {
          scheduleSync("fetch:" + url2);
        }
      } catch (_) {}

      return res;
    };
  }

  function patchXHR() {
    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__sockel_url = url;
      this.__sockel_method = method;
      return origOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      // track external cart mutations (xhr)
      try {
        const url = String(this.__sockel_url || "");
        const isCartMutation =
          url.includes("/cart/add") ||
          url.includes("/cart/change") ||
          url.includes("/cart/update") ||
          url.includes("/cart/clear");

        if (isCartMutation) {
          const p = new Promise((res) => this.addEventListener("loadend", res, { once: true }));
          trackExternalMutation(p.catch(() => {}));
        }
      } catch (_) {}

      this.addEventListener("load", () => {
        try {
          const url = String(this.__sockel_url || "");
          const isCartMutation =
            url.includes("/cart/add") ||
            url.includes("/cart/change") ||
            url.includes("/cart/update") ||
            url.includes("/cart/clear");

          if (isCartMutation && this.status >= 200 && this.status < 300) {
            scheduleSync("xhr:" + url);
          }
        } catch (_) {}
      });

      return origSend.apply(this, arguments);
    };
  }

  document.addEventListener("DOMContentLoaded", () => {
    patchFetch();
    patchXHR();

    scheduleSync("init");

    document.addEventListener("cart:refresh", () => scheduleSync("event:cart:refresh"));
    document.addEventListener("cart:change", () => scheduleSync("event:cart:change"));
    document.addEventListener("cart:updated", () => scheduleSync("event:cart:updated"));

    // Manual remove listener
    document.addEventListener(
      "click",
      (e) => {
        const path = typeof e.composedPath === "function" ? e.composedPath() : [];
        const btn = path.find(
          (node) =>
            node instanceof HTMLElement &&
            node.matches('button.js-remove-item[data-promo-sockel-remove="1"]')
        );

        if (!btn) return;

        const key = btn.getAttribute("data-cart-item-key");
        if (!key) return;

        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();

        handleManualRemovePromoSockel(key).catch((err) =>
          console.warn("[sockel] manual remove failed", err)
        );
      },
      true
    );
  });
})();
